import React, { useState, useEffect } from "react";
import "./NewAdmission.css";
import { apiService } from "./services/apiService";

const Field = ({ label, required, children }) => (
  <div className="na-field">
    <label className="na-label">
      {label}{required && <span className="na-required"> *</span>}
    </label>
    {children}
  </div>
);

function NewAdmission({ seat, onSuccess }) {
  const initialForm = {
    fullName: "",
    primaryContact: "",
    parentContact: "",
    registrationNo: "",
    collegeName: "",
    qualification: "",
    preparingFor: "",
    gender: "",
    dateOfBirth: "",
    email: "",
    parentEmail: "",
    permanentAddress: "",
    medicalInfo:"",
    correspondenceAddress: "",
    aadhaarNumber: "",
    referralContact: "",
    seatId: "", 
    startDate: "",
    endDate: "",
    feesPaid: ""
  };

  const [form, setForm] = useState(initialForm);
  const [seats, setSeats] = useState([]);
  
  // ✅ State to manage our glossy custom popup
  const [popup, setPopup] = useState({
    show: false,
    type: "success", // "success" | "error" | "warning"
    message: ""
  });

  // Helper to show popup
  const showAlert = (type, message) => {
    setPopup({ show: true, type, message });
  };

  // Helper to close popup
  const closePopup = () => {
    setPopup({ show: false, type: "success", message: "" });
    // If it was a successful admission, trigger redirect/refresh on close
    if (popup.type === "success" && popup.message.includes("Successfully")) {
      if (onSuccess) onSuccess();
    }
  };

  useEffect(() => {
    if (seat && seat.seatId) {
      setForm((prev) => ({
        ...prev,
        seatId: String(seat.seatId)
      }));
    }
  }, [seat]);

  useEffect(() => {
    apiService.getSeats()
      .then(data => setSeats(data || []))
      .catch(err => console.error("Failed to fetch seats:", err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // ✅ Enforce strict numeric-only keystrokes and a max limit of 10 digits for all phone fields
    if (["primaryContact", "parentContact", "referralContact"].includes(name)) {
      const onlyNums = value.replace(/\D/g, "");
      if (onlyNums.length <= 10) {
        setForm((prev) => ({ ...prev, [name]: onlyNums }));
      }
      return;
    }

    // ✅ Enforce strict numeric-only keystrokes and a max limit of 12 digits for Aadhaar
    if (name === "aadhaarNumber") {
      const onlyNums = value.replace(/\D/g, "");
      if (onlyNums.length <= 12) {
        setForm((prev) => ({ ...prev, [name]: onlyNums }));
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const aadhaarRegex = /^\d{12}$/;

    if (!form.fullName.trim()) return "Full Name is required";
    
    if (!form.primaryContact.trim()) return "Primary Contact is required";
    if (!phoneRegex.test(form.primaryContact.trim())) return "Primary Contact must be exactly a 10-digit mobile number";

    if (form.parentContact.trim() && !phoneRegex.test(form.parentContact.trim())) {
      return "Parent Contact must be exactly a 10-digit mobile number";
    }

    if (form.email.trim() && !emailRegex.test(form.email.trim())) {
      return "Please enter a valid email address (e.g., name@domain.com)";
    }

    if (form.parentEmail.trim() && !emailRegex.test(form.parentEmail.trim())) {
      return "Please enter a valid email address (e.g., name@domain.com)";
    }

    if (!form.aadhaarNumber.trim()) return "Aadhaar Number is required";
    if (!aadhaarRegex.test(form.aadhaarNumber.trim())) return "Aadhaar Number must be exactly a 12-digit number";

    // ✅ Added validation check for Referral Contact length constraints
    if (form.referralContact.trim() && !phoneRegex.test(form.referralContact.trim())) {
      return "Referral Contact must be exactly a 10-digit mobile number";
    }

    if (!form.seatId) return "Please select a seat";
    if (!form.startDate) return "Start Date is required";
    if (!form.endDate) return "End Date is required";
    
    if (!form.feesPaid || String(form.feesPaid).trim() === "") return "Fees is required";
    if (Number(form.feesPaid) < 0) return "Fees cannot be a negative number";

    if (new Date(form.endDate) <= new Date(form.startDate))
      return "End date must be after start date";
      
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) { 
      showAlert("warning", error); 
      return; 
    }

    try {
      const studentData = await apiService.createStudent({
        fullName: form.fullName,
        primaryContact: form.primaryContact,
        parentContact: form.parentContact,
        registrationNo: form.registrationNo,
        collegeName: form.collegeName,
        qualification: form.qualification,
        preparingFor: form.preparingFor,
        gender: form.gender,
        dateOfBirth: form.dateOfBirth,
        email: form.email,
        parentEmail: form.parentEmail,
        permanentAddress: form.permanentAddress,
        correspondenceAddress: form.correspondenceAddress,
        aadhaarNumber:form.aadhaarNumber,
        referralContact:form.referralContact,
        medicalInfo:form.medicalInfo
      });

      await apiService.createAdmission({
        studentId: studentData.id,
        seatId: form.seatId,
        startDate: form.startDate,
        endDate: form.endDate,
        feesPaid: Number(form.feesPaid)
      });

      showAlert("success", "Admission Created & Seat Booked Successfully!");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      showAlert("error", err.message || "An unexpected error occurred.");
    }
  };

  const handleReset = () => setForm(initialForm);

  return (
    <div className="na-page">
      <div className="na-form-card">

        {/* Header */}
        <div className="na-form-header">
          <div className="na-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
          </div>
          <div>
            <h2 className="na-form-title">New Admission</h2>
            <p className="na-form-subtitle">Fill in the details to register a new student</p>
          </div>
        </div>

        {/* Section: Student Information */}
        <div className="na-section">
          <div className="na-section-label">
            <span className="na-section-dot"></span>
            Student Information
          </div>

          <div className="na-grid-2">
            <Field label="Full Name" required>
              <input className="na-input" name="fullName"
                placeholder="Enter full name"
                value={form.fullName} onChange={handleChange} />
            </Field>
            <Field label="Registration No">
              <input className="na-input" name="registrationNo"
                placeholder="e.g. Auto Generated"
                value={form.registrationNo} disabled />
            </Field>
          </div>

          <div className="na-grid-2">
            <Field label="Primary Contact" required>
              <input className="na-input" name="primaryContact"
                type="tel"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={form.primaryContact} onChange={handleChange} />
            </Field>
            <Field label="Parent Contact">
              <input className="na-input" name="parentContact"
                type="tel"
                maxLength={10}
                placeholder="Parent's mobile number"
                value={form.parentContact} onChange={handleChange} />
            </Field>
          </div>

          <div className="na-grid-2">
            <Field label="Gender">
              <select className="na-input na-select" name="gender"
                value={form.gender} onChange={handleChange}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </Field>
            <Field label="Date of Birth">
              <input className="na-input" type="date" name="dateOfBirth"
                value={form.dateOfBirth} onChange={handleChange} />
            </Field>
          </div>

          <div className="na-grid-2">
          <Field label="Student Email">
            <input className="na-input" name="email"
              type="email"
              placeholder="student@email.com"
              value={form.email} onChange={handleChange} />
          </Field>

          <Field label="Parent Email">
            <input className="na-input" name="parentEmail"
              type="email"
              placeholder="parent@email.com"
              value={form.parentEmail} onChange={handleChange} />
          </Field>
         </div>

          <div className="na-grid-2">
           <Field label="Aadhaar Number" required>
            <input 
              className="na-input" 
              name="aadhaarNumber"
              type="tel"
              maxLength={12}
              placeholder="Enter 12-digit Aadhaar number"
              value={form.aadhaarNumber} 
              onChange={handleChange} 
            />
          </Field>
            <Field label="Referral Contact">
              <input className="na-input" name="referralContact"
                type="tel"
                maxLength={10}
                placeholder="Friend's mobile number"
                value={form.referralContact} onChange={handleChange} />
            </Field>
          </div>

          <Field label="Correspondence Address">
            <input className="na-input" name="correspondenceAddress"
              placeholder="Full Correspondence Address"
              value={form.correspondenceAddress} onChange={handleChange} />
          </Field>
        
          
          <Field label="Permanent Address">
            <input className="na-input" name="permanentAddress"
              placeholder="Full residential address"
              value={form.permanentAddress} onChange={handleChange} />
          </Field>
        </div>

          <Field label="Medical Info *">
            <input className="na-input" name="medicalInfo"
              placeholder="Medical info or Health Issue [NA]"
              value={form.medicalInfo} onChange={handleChange} />
          </Field>
        


        {/* Section: Academic Details */}
        <div className="na-section">
          <div className="na-section-label">
            <span className="na-section-dot"></span>
            Academic Details
          </div>

            <Field label="College Name">
              <input className="na-input" name="collegeName"
                placeholder="College / University name"
                value={form.collegeName} onChange={handleChange} />
            </Field>
            
          
          <div className="na-grid-2">
          <Field label="Qualification">
              <input className="na-input" name="qualification"
                placeholder="e.g.BE,BSC,BCOM,BCA,MCA,BA,HSC,SSC,OTHER"
                value={form.qualification} onChange={handleChange} />
            </Field>
          <Field label="Preparing For">
            <input className="na-input" name="preparingFor"
              placeholder="e.g. UPSC, MPSC, CA, NEET, BANKING, SSC, HSC, OTHER"
              value={form.preparingFor} onChange={handleChange} />
          </Field>
        </div>

        </div>

        {/* Section: Admission Details */}
        <div className="na-section">
          <div className="na-section-label">
            <span className="na-section-dot"></span>
            Admission Details
          </div>

          <Field label="Seat" required>
            <select className="na-input na-select" name="seatId"
              value={form.seatId} onChange={handleChange}>
              <option value="">Select a seat</option>
              {seats.map(seat => {
                let statusText = "";
                if (seat.status === "RENEWAL_PENDING") statusText = "Renewal Pending";
                else if (seat.currentCount === 0) statusText = "Available";
                else if (seat.currentCount < seat.maxCapacity) statusText = "1 Student";
                else statusText = "Full";

                return (
                  <option
                    key={seat.seatId}
                    value={seat.seatId}
                    disabled={seat.currentCount >= seat.maxCapacity || seat.blocked}
                  >
                    {seat.seatCode} — {statusText}
                  </option>
                );
              })}
            </select>
          </Field>

          <div className="na-grid-3">
            <Field label="Admission Date" required>
              <input className="na-input" type="date" name="startDate"
                value={form.startDate} onChange={handleChange} />
            </Field>
            <Field label="Expiry Date" required>
              <input className="na-input" type="date" name="endDate"
                value={form.endDate} onChange={handleChange} />
            </Field>
            <Field label="Fees Paid (₹)" required>
              <input 
                className="na-input" 
                type="number" 
                name="feesPaid"
                min="0"
                placeholder="0"
                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                value={form.feesPaid} 
                onChange={handleChange} 
              />
            </Field>
          </div>
        </div>

        {/* Actions */}
        <div className="na-actions">
          <button type="button" className="na-btn na-btn-reset" onClick={handleReset}>
            Reset
          </button>
          <button type="button" className="na-btn na-btn-submit" onClick={handleSubmit}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Submit Admission
          </button>
        </div>

      </div>

      {/* ✅ GLOSSY POPUP MODAL */}
      {popup.show && (
        <div className="na-popup-overlay" onClick={closePopup}>
          <div 
            className={`na-popup-card na-popup-${popup.type}`} 
            onClick={(e) => e.stopPropagation()} 
          >
            <div className="na-popup-glow"></div>
            
            <div className="na-popup-icon-container">
              {popup.type === "success" && (
                <div className="na-popup-icon na-pop-success">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
              )}
              {popup.type === "warning" && (
                <div className="na-popup-icon na-pop-warning">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
              )}
              {popup.type === "error" && (
                <div className="na-popup-icon na-pop-error">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}
            </div>

            <h3 className="na-popup-title">
              {popup.type === "success" && "Awesome!"}
              {popup.type === "warning" && "Heads Up!"}
              {popup.type === "error" && "Oops!"}
            </h3>
            
            <p className="na-popup-message">{popup.message}</p>
            
            <button className="na-popup-btn" onClick={closePopup}>
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewAdmission;